import { InsightPrompt, InsightType } from "@/data/interfaces/InsightType";
import adminService from "@/services/adminService";
import { useEffect, useState } from "react";
import { ErrorMessage } from "@/components/UI";
import TypeForm from "../Shared/PromptManagement/TypeForm";
import TypeList from "../Shared/PromptManagement/TypeList";
import PromptForm from "../Shared/PromptManagement/PromptForm";
import PromptList from "../Shared/PromptManagement/PromptList";
import { adminStatusBadgeClass } from "../../Shared/adminUi";

const ActionPrompts = () => {
  const [actionTypes, setActionTypes] = useState<InsightType[] | undefined>();
  const [selectedType, setSelectedType] = useState<InsightType | undefined>();
  const [prompts, setPrompts] = useState<InsightPrompt[] | undefined>();

  const [editingPrompt, setEditingPrompt] = useState<
    InsightPrompt | undefined
  >();
  const [editingActionType, setEditingActionType] = useState<
    InsightType | undefined
  >();

  const [errorMessage, setErrorMsg] = useState<string>();

  useEffect(() => {
    loadActionTypes();
  }, []);

  useEffect(() => {
    if (selectedType) {
      loadPrompts(selectedType.id);
    }
  }, [selectedType]);

  const loadActionTypes = async () => {
    try {
      const types = await adminService.actionPlanType.getAll();
      setActionTypes(types);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO GAUTI TIPŲ: ${error}`);
    }
  };

  const addNewType = async (data: Record<string, string | number | File>) => {
    try {
      setErrorMsg(undefined);
      await adminService.actionPlanType.create({
        name: String(data.name),
        description:
          typeof data.description === "string" ? data.description : undefined,
        image: data.image instanceof File ? data.image : undefined,
      });
      loadActionTypes();
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO PRIDĖTI NAUJO TIPO: ${error}`);
    }
  };

  const updateActionType = async (
    actionType: InsightType & { image?: string | File | null },
  ) => {
    try {
      setErrorMsg(undefined);
      await adminService.actionPlanType.update(
        actionType as Parameters<typeof adminService.actionPlanType.update>[0],
      );
      setEditingActionType(undefined);
      loadActionTypes();
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO ATNAUJINTI TIPĄ: ${error}`);
    }
  };

  const deleteActionType = async (type: InsightType) => {
    try {
      setErrorMsg(undefined);
      await adminService.actionPlanType.delete(type.id);
      loadActionTypes();
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO IŠTRINTI TIPO: ${error}`);
    }
  };

  const loadPrompts = async (typeId: number) => {
    try {
      const promptsData = await adminService.actionPlanPrompt.getByType(typeId);
      setPrompts(promptsData);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO GAUTI PROMPTŲ: ${error}`);
    }
  };

  const addNewPrompt = async (name: string, content: string) => {
    if (!selectedType) return;

    try {
      setErrorMsg(undefined);
      await adminService.actionPlanPrompt.create(
        selectedType.id,
        name,
        content,
      );
      loadPrompts(selectedType.id);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO PRIDĖTI NAUJO PROMPTO: ${error}`);
    }
  };

  const updatePrompt = async (prompt: InsightPrompt) => {
    try {
      setErrorMsg(undefined);
      await adminService.actionPlanPrompt.update(prompt);
      setEditingPrompt(undefined);

      if (!selectedType) return;

      loadPrompts(selectedType.id);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO ATNAUJINTI PROMPTĄ: ${error}`);
    }
  };

  const deletePrompt = async (prompt: InsightPrompt) => {
    try {
      setErrorMsg(undefined);
      await adminService.actionPlanPrompt.delete(prompt.id);

      if (!selectedType) return;

      loadPrompts(selectedType.id);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO IŠTRINTI PROMPTĄ: ${error}`);
    }
  };

  return (
    <>
      <div className="flex flex-col-reverse gap-8 md:flex-row md:gap-4">
        <div className="flex flex-col gap-2 md:w-fit md:min-w-[200px] lg:min-w-[350px]">
          {!selectedType ? (
            <TypeForm<InsightType>
              onSubmit={addNewType}
              onUpdate={updateActionType}
              editingType={editingActionType}
              onCancelEdit={() => setEditingActionType(undefined)}
              title="Pridėti naują tipą:"
              storageBaseUrl={
                import.meta.env.VITE_BASE_API_URL?.replace(/\/api\/?$/, "") +
                "/storage"
              }
              fields={[
                {
                  name: "description",
                  label: "Aprašymas",
                  type: "textarea",
                },
                {
                  name: "image",
                  label: "Nuotrauka",
                  type: "file",
                  accept: "image/*",
                  getValue: (t) => {
                    return t.image ?? "";
                  },
                },
              ]}
            />
          ) : (
            <PromptForm
              selectedType={selectedType}
              onSubmit={addNewPrompt}
              onUpdate={updatePrompt}
              onBack={() => setSelectedType(undefined)}
              editingPrompt={editingPrompt}
              onCancelEdit={() => setEditingPrompt(undefined)}
            />
          )}

          <ErrorMessage error={errorMessage} />
        </div>

        {!selectedType ? (
          <TypeList
            types={actionTypes}
            onSelectType={setSelectedType}
            onDeleteType={deleteActionType}
            onEditType={setEditingActionType}
            title="Action Plan Tipai"
            storageBaseUrl={
              import.meta.env.VITE_BASE_API_URL?.replace(/\/api\/?$/, "") +
              "/storage"
            }
            getImage={(type) => type.image}
            getDescription={(type) => type.description}
            renderTypeBadges={(type) => (
              <span className={adminStatusBadgeClass(!!type.is_active)}>
                {type.is_active ? "Aktyvus" : "Neaktyvus"}
              </span>
            )}
          />
        ) : (
          <PromptList
            prompts={prompts}
            selectedType={selectedType}
            onDeletePrompt={deletePrompt}
            onEditPrompt={setEditingPrompt}
            title="Action Plan Prompts"
            renderPromptBadges={(prompt) => (
              <span className={adminStatusBadgeClass(!!prompt.is_active)}>
                {prompt.is_active ? "Aktyvus" : "Neaktyvus"}
              </span>
            )}
          />
        )}
      </div>
    </>
  );
};

export default ActionPrompts;
